import {isEqual} from 'lodash';

import {isDefined} from '../../../libs/shared/helpers/src/typescript/typeHelpers';
import {decorateWithClientId, WithClientId} from '../editors/commons/clientIdModel';
import {EditorValidationErrorsEnum} from '../editors/commons/editorErrors';
import {ParameterId} from '../editors/commons/parameterModel';
import {RuleAction} from './ruleActionModel';
import {RuleCondition} from './ruleConditionModel';
import {RuleActionDefinition, RuleConditionDefinition} from './ruleParameterModel';

export interface RuleActionConditionItemOptions {
  shouldDisplayPlanPill?: boolean;
}

export enum RuleDefaultCardTypesEnum {
  CONDITION,
  ACTION,
}

// Build a generic empty / default item from a definition spec.
export function fromDefinitionItem<
  Item extends RuleConditionDefinition | RuleActionDefinition,
  Result extends Item extends RuleConditionDefinition ? RuleCondition : RuleAction,
>(definitionItem: Item, clientId?: string): WithClientId<Result> {
  const params: Record<ParameterId, unknown> = {};

  Object.entries(definitionItem.parameters).forEach(([parameterId, parameter]) => {
    const resolvedParam = definitionItem.resolvedParameters[parameterId] ?? parameter;

    const isParamPublic = Boolean(!('public' in resolvedParam) || resolvedParam?.public);
    // Ignore explicitly private parameters.
    if (!isParamPublic) {
      return;
    }

    params[parameterId] =
      'default' in resolvedParam ? resolvedParam?.default : definitionItem.defaultParams[parameterId];
  });

  const ruleActionCondition = {
    type: definitionItem.id,
    integration_id: definitionItem.integration_id,
    params,
  } as unknown as Result;

  return clientId ? {...ruleActionCondition, clientId} : decorateWithClientId(ruleActionCondition);
}

interface WithTypeAndParams {
  type: string;
  params?: any;
}

export function typeAndParamsEqual<T extends WithTypeAndParams>(a: T, b: T): boolean {
  if (a.type !== b.type) {
    return false;
  }

  const paramsA = (a as any).params;
  const paramsB = (b as any).params;

  // Resort to deep equality check for params.
  // Note : Params are shallow and do not contain many items.
  return isEqual(paramsA, paramsB);
}

export function getParamsErrors<T extends WithTypeAndParams>(
  t: T,
): ReadonlyArray<EditorValidationErrorsEnum> {
  const params = 'params' in t && (t.params ?? {});

  return Object.values(params)
    .map((value: any): EditorValidationErrorsEnum | undefined => {
      if (!isDefined(value)) {
        return EditorValidationErrorsEnum.UNDEFINED_PARAMETER;
      }

      if (Array.isArray(value) && value.length === 0) {
        return EditorValidationErrorsEnum.EMPTY_ARRAY;
      }

      if (typeof value === 'string' && value.length === 0) {
        return EditorValidationErrorsEnum.EMPTY_STRING;
      }

      return undefined;
    })
    .filter(isDefined);
}
